/**
 * The KPI strip: one row, three tiles, no prose.
 *
 * Paid today is the money question. Pipeline is five counts in one segmented tile, each a
 * click into that queue. Problems is four sub-counts, all of which land in Needs attention
 * because that is the one place they are worked.
 *
 * No tile computes anything. Every number is a field on the page response, so the count on
 * a tile and the count in the rail are the same derivation in Postgres rather than two
 * that drift by lunchtime. What each one means lives in the hover popover and on /legend,
 * nowhere else on this page.
 */
import type { OrderQueue, OrdersPageKpis } from '@elutide/shared';
import { DefinitionPopover, KpiTile } from '@/components';
import { formatCount, formatMoney } from '@/lib/format';
import { definitionFor, type LegendIndex } from '@/lib/legend';
import type { OrdersPageKpisView } from './types';

export interface OrdersKpiStripProps {
  /**
   * `paid_today` is absent for a packer and an agent (`redact.ts`), and the tile goes with
   * it: not a blank tile, not a dash, nothing that says a number exists. The two count
   * tiles are queue work and stay for every role.
   */
  kpis: OrdersPageKpisView;
  legend: LegendIndex;
  /** The queue the rail is on, so a segment can show itself as the active one. */
  queue: OrderQueue;
  onQueue: (queue: OrderQueue) => void;
}

const PIPELINE: readonly { id: OrderQueue; label: string; key: keyof OrdersPageKpis['pipeline'] }[] =
  [
    { id: 'awaiting_payment', label: 'Awaiting payment', key: 'awaiting_payment' },
    { id: 'to_print', label: 'To print', key: 'to_print' },
    { id: 'to_pack', label: 'To pack', key: 'to_pack' },
    { id: 'packed', label: 'Packed', key: 'packed' },
    { id: 'in_transit', label: 'In transit', key: 'in_transit' },
  ];

const PIPELINE_LEGEND: Readonly<Record<string, string>> = {
  awaiting_payment: 'orders.awaiting_payment',
  to_print: 'orders.to_print',
  to_pack: 'orders.to_pack',
  packed: 'orders.packed',
  in_transit: 'orders.in_transit',
};

const PROBLEMS: readonly {
  key: keyof OrdersPageKpis['problems'];
  label: string;
  legendId: string;
}[] = [
  { key: 'compliance_hold', label: 'Compliance hold', legendId: 'orders.problems' },
  { key: 'unmatched_payments', label: 'Unmatched payments', legendId: 'orders.unmatched_payment' },
  { key: 'address_flagged', label: 'Address flagged', legendId: 'orders.problems' },
  { key: 'stuck', label: 'Stuck', legendId: 'orders.needs_attention' },
];

export function OrdersKpiStrip({ kpis, legend, queue, onQueue }: OrdersKpiStripProps) {
  const def = (id: string) => definitionFor(legend, id);
  const paid = kpis.paid_today;
  const problemTotal =
    kpis.problems.compliance_hold +
    kpis.problems.unmatched_payments +
    kpis.problems.address_flagged +
    kpis.problems.stuck;

  return (
    <div className="ord-kpis" role="group" aria-label="Orders today" data-money={paid ? 'true' : 'false'}>
      {paid ? (
        <KpiTile
          label="Paid today"
          value={paid.cents}
          unit="cents"
          showCents
          showProvenance={false}
          definition={def('orders.paid_today')}
          delta={{ text: `${formatCount(paid.count)} orders` }}
          note={`yesterday ${formatMoney(paid.yesterday_cents)} . 7-day avg ${formatMoney(paid.avg7_cents)}`}
        />
      ) : null}

      <DefinitionPopover definition={def('orders.pipeline')} id="orders-pipeline">
        <section className="ord-seg" role="group" aria-label="Pipeline">
          <h3 className="ord-seg__label">Pipeline</h3>
          <div className="ord-seg__row">
            {PIPELINE.map((stage) => (
              <button
                key={stage.id}
                type="button"
                className="ord-seg__cell"
                aria-pressed={queue === stage.id}
                onClick={() => onQueue(stage.id)}
                title={def(PIPELINE_LEGEND[stage.id] ?? '') ?? undefined}
              >
                <span className="ord-seg__value">{formatCount(kpis.pipeline[stage.key])}</span>
                <span className="ord-seg__cell-label">{stage.label}</span>
              </button>
            ))}
          </div>
        </section>
      </DefinitionPopover>

      <DefinitionPopover definition={def('orders.needs_attention')} id="orders-problems">
        <section
          className="ord-seg ord-seg--problems"
          role="group"
          aria-label="Problems"
          data-clear={problemTotal === 0 ? 'true' : 'false'}
        >
          <h3 className="ord-seg__label">
            Problems
            <span className="ord-seg__total">{formatCount(problemTotal)}</span>
          </h3>
          <div className="ord-seg__row">
            {PROBLEMS.map((problem) => {
              const count = kpis.problems[problem.key];
              return (
                <button
                  key={problem.key}
                  type="button"
                  className="ord-seg__cell"
                  data-live={count > 0 ? 'true' : 'false'}
                  aria-pressed={queue === 'needs_attention'}
                  onClick={() => onQueue('needs_attention')}
                  title={def(problem.legendId) ?? undefined}
                >
                  <span className="ord-seg__value">{formatCount(count)}</span>
                  <span className="ord-seg__cell-label">{problem.label}</span>
                </button>
              );
            })}
          </div>
        </section>
      </DefinitionPopover>
    </div>
  );
}
